(ns hai.notes.editor
  "The assembled note-editing surfaces: the sticky-yellow paper holding the
   text form, the tags/links sections, and the save-status/share footer — as a
   desktop edit modal with a tinted header ([[modal-content]] in [[dialog]])
   or a mobile full-screen editor ([[screen]]). Hosts supply the note data and
   save wiring as props — the home cards open the editor on the scratchpad,
   the creation flow on a fresh standalone note."
  (:require
   [hai.entity.kebab :as kebab]
   [hai.entity.sections :as sections]
   [hai.notes.card :as note-card]
   [hai.notes.form :as form]
   [hai.notes.header :as header]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(defn tag-save-payload
  "Save payload for a picked tag — an existing tag travels by uuid, a new one
   by name."
  [tag]
  (if-let [tag-uuid (:group/uuid tag)]
    {:tag-uuids [tag-uuid]}
    {:tag-names [(:tag/name tag)]}))

(defn body
  "The editing innards every expanded-editor host renders: the auto-saving
   text form, the tags/links/contacts sections, and the save/share footer.
   Hosts wrap it in their own styled container, which styles `textarea.body`.
   `:editing?` (default true) renders the whole surface read-only for viewers
   without edit rights; `:share?` (default true) gates the footer's share
   button."
  [{:keys [note tags link-urls contacts linked-notes linked-tasks saving? form-id
           !autosave auto-focus? editing? share? on-save-text extras-handlers
           on-share-opened on-share-closed]
    :or {editing? true share? true}}]
  [:<>
   [form/note-form
    {:form-id      form-id
     :note         note
     :resume-text? true
     :editing?     editing?
     :auto-focus?  auto-focus?
     :!autosave    !autosave
     :placeholder  "Jot anything down… saves as you type."
     :on-save-text on-save-text}]
   [form/note-extras
    (merge extras-handlers
           {:editing?     editing?
            :tags         tags
            :link-urls    link-urls
            :contacts     contacts
            :linked-notes linked-notes
            :linked-tasks linked-tasks
            :note-uuid    (:note/uuid note)})]
   [note-card/note-footer
    {:note            note
     :saving?         saving?
     :share?          share?
     :on-share-opened on-share-opened
     :on-share-closed on-share-closed}]])

;; The editor's yellow paper — hosts inherit it so the surface color lives in
;; one place.
(o/defstyled paper :div
  {:background t/--sticky-yellow-light})

(o/defstyled edit-modal paper
  {:border-radius "16px"
   :overflow "hidden"
   :display "flex" :flex-direction "column"}
  [:.modal-body
   {:padding "20px 24px 18px"
    :display "flex" :flex-direction "column" :gap "16px"}]
  [:textarea.body
   {:width "100%" :box-sizing "border-box"
    :background "transparent" :border "none" :outline "none" :resize "none"
    :padding "0" :margin "0"
    :font-family "inherit" :font-size "15px" :font-weight "500" :line-height "1.6"
    :color t/--charcoal
    :min-height "320px"
    :max-height "80vh"
    :overflow-y "auto"
    :field-sizing "content"}
   [:&::placeholder {:color t/--note-sage-55}]])

;; Slide-up entrance for the full-screen editor. Garden keyframes are evaluated
;; at macro time, so garden.stylesheet needs no cljs require here.
#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules edit-screen-in
  (garden.stylesheet/at-keyframes
   :note-edit-screen-in
   [:from {:transform "translateY(100%)"}]
   [:to {:transform "translateY(0)"}]))

(o/defstyled edit-screen paper
  {:position "fixed" :inset "0"
   :z-index "200"
   :display "flex" :flex-direction "column"
   :overscroll-behavior "contain"
   :animation "note-edit-screen-in 300ms cubic-bezier(.2,.7,.3,1)"}
  ;; The full-screen header sizes its buttons (and the ⋮ beside them) up to
  ;; 32px for touch.
  [header/bar
   {:flex "0 0 auto"
    :padding "14px 16px"}
   [:.bar-action-btn {:width "32px" :height "32px"}]
   [sections/kebab-wrap
    [:button
     {:width "32px" :height "32px" :border-radius "999px"
      :color t/--note-kraft}
     [:&:hover {:background t/--note-kraft-10 :color t/--note-kraft}]
     [:svg {:width "20px" :height "20px"}]]]]
  [:.edit-body
   {:flex "1 1 auto" :min-height "0"
    :padding "16px"
    :display "flex" :flex-direction "column" :gap "14px"}]
  [:textarea.body
   {:width "100%" :box-sizing "border-box"
    :background "transparent" :border "none" :outline "none" :resize "none"
    :padding "0" :margin "0"
    :font-family "inherit" :font-size "16px" :font-weight "500" :line-height "1.6"
    :color t/--charcoal
    :flex "1 1 auto" :min-height "0"
    :overflow-y "auto"
    :overscroll-behavior "contain"}
   [:&::placeholder {:color t/--note-sage-55}]]
  ;; Tags/links and the footer sit below the flexing text area at natural height.
  [#{sections/extras-wrap sections/add-section-row note-card/note-footer-styled}
   {:flex "0 0 auto"}])

(def ^:private visually-hidden
  {:position "absolute" :width "1px" :height "1px" :padding "0" :margin "-1px"
   :overflow "hidden" :clip "rect(0 0 0 0)" :white-space "nowrap" :border "0"})

(defn dialog
  "Radix dialog shell for [[modal-content]]: a transparent panel so the sticky
   is the only visible surface, with the title kept for a11y."
  [{:keys [open? on-close title]} & children]
  [c/DialogRoot
   {:open open?
    :onOpenChange (fn [opening?] (when-not opening? (on-close)))}
   (into [c/DialogContent {:style {:background "transparent" :box-shadow "none"
                                   :padding "0" :max-width "900px" :overflow "visible"}}
          [c/DialogTitle {:style visually-hidden} title]]
         children)])

(defn modal-content
  "The editor surface — in a dialog or straight on a page. `extras-handlers`
   are the tag/link/contact save handlers for [[hai.notes.form/note-extras]];
   `!autosave` (optional) receives the form's pending-save controls; the ⋮
   shows once the note exists and `on-delete` is given; the ✕ shows when
   `on-close` is given. `:editing?`/`:share?` (default true) render the
   surface read-only / hide the share button."
  [{:keys [title note tags link-urls contacts linked-notes linked-tasks saving?
           form-id !autosave editing? share? auto-focus? on-save-text
           extras-handlers on-share-opened on-share-closed on-close on-delete]
    :or {editing? true share? true auto-focus? true}}]
  [edit-modal
   [header/bar {:title title}
    (when (and on-delete (:note/uuid note))
      [kebab/menu
       {:on-delete      on-delete
        :delete-label   "Delete note"
        :confirm-prompt "Delete this note?"
        :aria-label     "Note actions"}])
    (when on-close
      [c/Tooltip {:content "Close"}
       [:button.bar-action-btn
        {:type "button"
         :aria-label "Close"
         :on-click on-close}
        [icons/outlined-close {:size 16}]]])]
   [:div.modal-body
    [body
     {:note            note
      :tags            tags
      :link-urls       link-urls
      :contacts        contacts
      :linked-notes    linked-notes
      :linked-tasks    linked-tasks
      :saving?         saving?
      :form-id         form-id
      :!autosave       !autosave
      :editing?        editing?
      :share?          share?
      :auto-focus?     auto-focus?
      :on-save-text    on-save-text
      :extras-handlers extras-handlers
      :on-share-opened on-share-opened
      :on-share-closed on-share-closed}]]])

(defn screen
  "The full-screen (mobile) editor surface — same contract as
   [[modal-content]]. A fixed overlay, not a dialog: it holds the page body
   still while mounted so nothing behind it scrolls."
  [{:keys [title note tags link-urls contacts linked-notes linked-tasks saving?
           form-id !autosave editing? share? auto-focus? on-save-text
           extras-handlers on-share-opened on-share-closed on-close on-delete]
    :or {editing? true share? true auto-focus? true}}]
  (r/with-let [body-overflow (let [body-style (.. js/document -body -style)
                                   previous (.-overflow body-style)]
                               (set! (.-overflow body-style) "hidden")
                               previous)]
    [edit-screen
     [header/bar {:title title :dotted? true}
      (when (and on-delete (:note/uuid note))
        [kebab/menu
         {:on-delete      on-delete
          :delete-label   "Delete note"
          :confirm-prompt "Delete this note?"
          :aria-label     "Note actions"}])
      [:button.bar-action-btn
       {:type "button" :aria-label "Close" :on-click on-close}
       [icons/outlined-close {:size 20}]]]
     [:div.edit-body
      [body
       {:note            note
        :tags            tags
        :link-urls       link-urls
        :contacts        contacts
        :linked-notes    linked-notes
        :linked-tasks    linked-tasks
        :saving?         saving?
        :form-id         form-id
        :!autosave       !autosave
        :editing?        editing?
        :share?          share?
        :auto-focus?     auto-focus?
        :on-save-text    on-save-text
        :extras-handlers extras-handlers
        :on-share-opened on-share-opened
        :on-share-closed on-share-closed}]]]
    (finally
      (set! (.. js/document -body -style -overflow) body-overflow))))
